/**
 * Configurazione dei widget: quali moduli mostrare (Home/promemoria, timer,
 * progressi, impegni) e da quale profilo dell'account attingere i dati.
 *
 * Solo funzioni pure + una piccola persistenza locale, così la stessa
 * configurazione è leggibile dalla pagina widget, dai widget in-app e dalla
 * sincronizzazione verso i widget nativi del telefono.
 */
import { isRole, type AccountRole } from "@/lib/role-access";

export const WIDGET_CONFIG_KEY = "lumen.widget-config.v1";

export type WidgetModuleId = "reminder" | "timer" | "progress" | "agenda";

export const WIDGET_MODULES: WidgetModuleId[] = ["reminder", "timer", "progress", "agenda"];

export type WidgetConfig = {
  version: 1;
  modules: Record<WidgetModuleId, boolean>;
  /** Profilo dell'account da cui il widget prende i dati. */
  sourceRole: AccountRole;
};

export const DEFAULT_WIDGET_CONFIG: WidgetConfig = {
  version: 1,
  modules: { reminder: true, timer: true, progress: true, agenda: false },
  sourceRole: "student",
};

/** Moduli che hanno senso solo per lo studente (streak, XP, promemoria di studio). */
const STUDENT_ONLY: WidgetModuleId[] = ["progress"];

export function isModuleAvailable(id: WidgetModuleId, role: AccountRole): boolean {
  return role === "student" || !STUDENT_ONLY.includes(id);
}

function isModuleId(v: unknown): v is WidgetModuleId {
  return typeof v === "string" && (WIDGET_MODULES as string[]).includes(v);
}

/** Lettura difensiva: valori mancanti o non validi ricadono sui default. */
export function normalizeWidgetConfig(raw: unknown, allowedRoles?: AccountRole[]): WidgetConfig {
  const obj = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const rawModules = (obj["modules"] && typeof obj["modules"] === "object" ? obj["modules"] : {}) as Record<
    string,
    unknown
  >;

  const rawRole = obj["sourceRole"];
  let sourceRole: AccountRole = isRole(rawRole) ? rawRole : DEFAULT_WIDGET_CONFIG.sourceRole;
  if (allowedRoles?.length && !allowedRoles.includes(sourceRole)) sourceRole = allowedRoles[0]!;

  const modules = { ...DEFAULT_WIDGET_CONFIG.modules };
  for (const key of Object.keys(rawModules)) {
    if (isModuleId(key)) modules[key] = Boolean(rawModules[key]);
  }
  for (const id of WIDGET_MODULES) {
    if (!isModuleAvailable(id, sourceRole)) modules[id] = false;
  }
  // Almeno un modulo deve restare attivo, altrimenti il widget resta vuoto.
  if (!WIDGET_MODULES.some((id) => modules[id])) {
    const fallback = WIDGET_MODULES.find((id) => isModuleAvailable(id, sourceRole)) ?? "reminder";
    modules[fallback] = true;
  }
  return { version: 1, modules, sourceRole };
}

/** Attiva/disattiva un modulo mantenendone almeno uno acceso. */
export function toggleModule(config: WidgetConfig, id: WidgetModuleId, on: boolean): WidgetConfig {
  if (!on && enabledModules(config).length === 1 && config.modules[id]) return config;
  if (on && !isModuleAvailable(id, config.sourceRole)) return config;
  return normalizeWidgetConfig({ ...config, modules: { ...config.modules, [id]: on } }, undefined);
}

/** Cambia il profilo sorgente, spegnendo i moduli non disponibili per quel ruolo. */
export function setSourceRole(config: WidgetConfig, role: AccountRole): WidgetConfig {
  return normalizeWidgetConfig({ ...config, sourceRole: role }, [role]);
}

export function enabledModules(config: WidgetConfig): WidgetModuleId[] {
  return WIDGET_MODULES.filter((id) => config.modules[id]);
}

export function configsEqual(a: WidgetConfig, b: WidgetConfig): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/* ---------------------------------- storage --------------------------------- */

export function readWidgetConfig(allowedRoles?: AccountRole[]): WidgetConfig {
  if (typeof window === "undefined") return DEFAULT_WIDGET_CONFIG;
  try {
    const raw = window.localStorage.getItem(WIDGET_CONFIG_KEY);
    return normalizeWidgetConfig(raw ? JSON.parse(raw) : null, allowedRoles);
  } catch {
    return DEFAULT_WIDGET_CONFIG;
  }
}

export function writeWidgetConfig(config: WidgetConfig): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(WIDGET_CONFIG_KEY, JSON.stringify(config));
    window.dispatchEvent(new CustomEvent("lumen:widget-config"));
  } catch {
    /* storage pieno o bloccato: la configurazione resta solo in memoria */
  }
}
